// dev24-youtube-playlist-sync — playlist CRUD + sync UI.
//
// Structurally parallel to YTChannelManager but simpler: playlists don't
// have TBTV-channel / CF-live-input / viloud defaults (those apply to
// live-event ingestion; playlist sync is VOD-only for now — items land
// in youtube_videos with include_in_schedule=0 and are promoted to catalog
// individually).
//
// Backing routes (all under /api/youtube/archive/playlists/*):
//   GET     — list all + video counts
//   POST    — add (auto-fetches YT name if not supplied)
//   PATCH   — edit name / api_key / enabled / sync_window_days
//   DELETE  — remove playlist row + join rows; leaves youtube_videos intact
//   POST /sync              — sync all enabled
//   POST /:id/sync          — sync one

import { useState, useEffect, useCallback } from 'react'
import { X, Plus, Trash2, RefreshCw, Save, Edit2 } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

export default function YTPlaylistManager({ onClose, onUpdate }) {
  const { api } = useAuth()

  const [playlists, setPlaylists] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState(null)
  const [syncing, setSyncing] = useState(new Set()) // per-playlist Sync busy flag
  const [syncingAll, setSyncingAll] = useState(false)

  const [newPlaylist, setNewPlaylist] = useState({
    youtube_playlist_id: '',
    name: '',
    api_key: '',
    sync_window_days: 14,
    enabled: true,
    auto_migrate_thumbnails: false,  // dev24: opt-in per source
    sync_all: false,                 // "Sync entire playlist" — ignore the day window
  })

  const [editForm, setEditForm] = useState({})

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api('/youtube/archive/playlists')
      setPlaylists(data.playlists || [])
    } catch (e) {
      setStatus(`Error loading: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { load() }, [load])

  async function handleAdd() {
    if (!newPlaylist.youtube_playlist_id.trim()) {
      setStatus('Error: Playlist ID required')
      return
    }
    setStatus('Adding playlist…')
    try {
      const r = await api('/youtube/archive/playlists', {
        method: 'POST',
        body: JSON.stringify({
          youtube_playlist_id: newPlaylist.youtube_playlist_id.trim(),
          name: newPlaylist.name.trim() || undefined,
          api_key: newPlaylist.api_key.trim() || undefined,
          sync_window_days: parseInt(newPlaylist.sync_window_days, 10) || 14,
          enabled: newPlaylist.enabled,
          auto_migrate_thumbnails: newPlaylist.auto_migrate_thumbnails,
          sync_all: newPlaylist.sync_all,
        }),
      })
      setStatus(`✅ Added: ${r.name || r.youtube_playlist_id}`)
      setNewPlaylist({ youtube_playlist_id: '', name: '', api_key: '', sync_window_days: 14, enabled: true, auto_migrate_thumbnails: false, sync_all: false })
      setShowAdd(false)
      await load()
      onUpdate?.()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function startEdit(pl) {
    setEditing(pl.youtube_playlist_id)
    setEditForm({
      name: pl.name || '',
      api_key: pl.api_key || '',
      sync_window_days: pl.sync_window_days || 14,
      enabled: !!pl.enabled,
      auto_migrate_thumbnails: !!pl.auto_migrate_thumbnails,
      sync_all: !!pl.sync_all,
    })
  }

  async function handleSaveEdit() {
    setStatus('Saving…')
    try {
      await api(`/youtube/archive/playlists/${editing}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: editForm.name.trim(),
          api_key: editForm.api_key.trim() || null,
          sync_window_days: parseInt(editForm.sync_window_days, 10) || 14,
          enabled: editForm.enabled,
          auto_migrate_thumbnails: editForm.auto_migrate_thumbnails,
          sync_all: editForm.sync_all,
        }),
      })
      setStatus('✅ Saved')
      setEditing(null)
      await load()
      onUpdate?.()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleDelete(pl) {
    if (!confirm(`Delete playlist "${pl.name}"?\n\nThe playlist row and its video attribution mapping will be removed. ${pl.video_count || 0} video(s) will remain in the YouTube Archive (and stay linked to catalog if promoted).`)) {
      return
    }
    setStatus(`Deleting ${pl.name}…`)
    try {
      await api(`/youtube/archive/playlists/${pl.youtube_playlist_id}`, { method: 'DELETE' })
      setStatus(`✅ Deleted: ${pl.name}`)
      await load()
      onUpdate?.()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleSyncOne(pl) {
    setSyncing(prev => new Set(prev).add(pl.youtube_playlist_id))
    setStatus(`Syncing "${pl.name}" (${pl.sync_all ? 'entire playlist' : `last ${pl.sync_window_days || 14} days`})…`)
    try {
      const r = await api(`/youtube/archive/playlists/${pl.youtube_playlist_id}/sync`, { method: 'POST' })
      setStatus(`✅ ${pl.name}: +${r.added} added, ${r.preserved} preserved, ${r.playlistMapped} mapped, ${r.skippedOutOfWindow} out-of-window`)
      await load()
      onUpdate?.()
    } catch (e) {
      setStatus(`Error syncing ${pl.name}: ${e.message}`)
    } finally {
      setSyncing(prev => {
        const next = new Set(prev)
        next.delete(pl.youtube_playlist_id)
        return next
      })
    }
  }

  async function handleSyncAll() {
    setSyncingAll(true)
    setStatus('Syncing all enabled playlists…')
    try {
      const r = await api('/youtube/archive/playlists/sync', { method: 'POST' })
      const summary = (r.results || []).map(x =>
        x.error ? `${x.playlist}: ERROR ${x.error}` : `${x.playlist}: +${x.added}/${x.preserved}`
      ).join(' • ')
      setStatus(`✅ Synced ${(r.results || []).length}: ${summary || '(none)'}`)
      await load()
      onUpdate?.()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setSyncingAll(false)
    }
  }

  async function handleToggleEnabled(pl) {
    try {
      await api(`/youtube/archive/playlists/${pl.youtube_playlist_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !pl.enabled }),
      })
      await load()
      onUpdate?.()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="yt-channel-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>YouTube Source Playlists</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn-primary"
              onClick={handleSyncAll}
              disabled={syncingAll || playlists.length === 0}
              title="Sync all enabled playlists"
            >
              <RefreshCw size={14} className={syncingAll ? 'spinning' : ''} />
              {syncingAll ? 'Syncing all…' : 'Sync All'}
            </button>
            <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        {status && (
          <div className={`yt-detail-status ${status.startsWith('Error') ? 'error' : ''}`}>
            {status}
          </div>
        )}

        {loading ? (
          <div className="yt-channel-list"><div style={{ padding: 12, color: '#888' }}>Loading…</div></div>
        ) : (
          <div className="yt-channel-list">
            {playlists.length === 0 && !showAdd && (
              <div style={{ padding: 12, color: '#888' }}>
                No playlists yet. Add a playlist ID below to start syncing.
              </div>
            )}
            {playlists.map(pl => (
              <div key={pl.youtube_playlist_id} className={`yt-channel-item ${!pl.enabled ? 'disabled' : ''}`}>
                {editing === pl.youtube_playlist_id ? (
                  <div className="yt-channel-edit-form">
                    <div className="form-row"><label>Name</label>
                      <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} /></div>
                    <div className="form-row"><label>Sync Window (days)</label>
                      <input type="number" min="1" value={editForm.sync_window_days} disabled={editForm.sync_all}
                        onChange={e => setEditForm({ ...editForm, sync_window_days: e.target.value })} /></div>
                    <div className="form-row"><label>API Key Override</label>
                      <input value={editForm.api_key} placeholder="(uses YOUTUBE_API_KEY when blank)"
                        onChange={e => setEditForm({ ...editForm, api_key: e.target.value })} /></div>
                    <div className="form-row checkbox-row"><label>
                      <input type="checkbox" checked={editForm.enabled}
                        onChange={e => setEditForm({ ...editForm, enabled: e.target.checked })} /> Enabled
                    </label></div>
                    {/* dev24: opt-in per-source thumbnail migration to CF Images */}
                    <div className="form-row checkbox-row"><label title="On each sync, upload the YouTube thumbnail to Cloudflare Images (16:9). Off by default. Roku-spec-compliant thumbnails will land in the catalog without a manual migration step.">
                      <input type="checkbox" checked={editForm.auto_migrate_thumbnails || false}
                        onChange={e => setEditForm({ ...editForm, auto_migrate_thumbnails: e.target.checked })} /> Auto-migrate thumbnails to CF Images (16:9)
                    </label></div>
                    <div className="form-row checkbox-row"><label title="Ignore the day window and sync the full playlist. Best for static playlists that aren't updated frequently.">
                      <input type="checkbox" checked={editForm.sync_all || false}
                        onChange={e => setEditForm({ ...editForm, sync_all: e.target.checked })} /> Sync entire playlist (ignore window)
                    </label></div>
                    <div className="yt-channel-edit-actions">
                      <button className="btn-primary" onClick={handleSaveEdit}><Save size={12} /> Save</button>
                      <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="yt-channel-info">
                      <div className="yt-channel-name">{pl.name}</div>
                      <div className="yt-channel-id">{pl.youtube_playlist_id}</div>
                      <div className="yt-channel-meta">
                        <span title={pl.sync_all ? 'Syncs the entire playlist' : 'Rolling sync window'}>🗓 {pl.sync_all ? 'entire playlist' : `last ${pl.sync_window_days || 14}d`}</span>
                        <span title="Videos attributed to this playlist">📼 {pl.video_count || 0} video{pl.video_count === 1 ? '' : 's'}</span>
                        {pl.last_synced_at && (
                          <span title={pl.last_synced_at}>🔄 {new Date(pl.last_synced_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="yt-channel-sync-toggle" title={pl.enabled ? 'Sync enabled — click to disable' : 'Sync disabled — click to enable'}>
                      <label className="sync-switch">
                        <input type="checkbox" checked={!!pl.enabled} onChange={() => handleToggleEnabled(pl)} />
                        <span className="sync-slider" />
                      </label>
                      <span className={`sync-label ${pl.enabled ? 'on' : 'off'}`}>
                        {pl.enabled ? 'Sync on' : 'Sync off'}
                      </span>
                    </div>
                    <div className="yt-channel-actions">
                      <button className="btn-ghost btn-sm" onClick={() => handleSyncOne(pl)}
                        disabled={syncing.has(pl.youtube_playlist_id)} title={pl.sync_all ? 'Sync entire playlist' : `Sync last ${pl.sync_window_days || 14} days`}>
                        <RefreshCw size={14} className={syncing.has(pl.youtube_playlist_id) ? 'spinning' : ''} />
                      </button>
                      <button className="btn-ghost btn-sm" onClick={() => startEdit(pl)} title="Edit">
                        <Edit2 size={14} />
                      </button>
                      <button className="btn-ghost btn-sm" onClick={() => handleDelete(pl)} title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add form */}
        {showAdd ? (
          <div className="yt-add-form">
            <h3>Add YouTube Playlist</h3>
            <div className="form-row"><label>Playlist ID *</label>
              <input value={newPlaylist.youtube_playlist_id}
                onChange={e => setNewPlaylist({ ...newPlaylist, youtube_playlist_id: e.target.value })}
                placeholder="PL... (from a YouTube playlist URL)" /></div>
            <div className="form-row"><label>Name (auto-fetched if blank)</label>
              <input value={newPlaylist.name}
                onChange={e => setNewPlaylist({ ...newPlaylist, name: e.target.value })}
                placeholder="Optional display name" /></div>
            <div className="form-row"><label>Sync Window (days)</label>
              <input type="number" min="1" value={newPlaylist.sync_window_days} disabled={newPlaylist.sync_all}
                onChange={e => setNewPlaylist({ ...newPlaylist, sync_window_days: e.target.value })} /></div>
            <div className="form-row"><label>API Key Override</label>
              <input value={newPlaylist.api_key}
                onChange={e => setNewPlaylist({ ...newPlaylist, api_key: e.target.value })}
                placeholder="(uses YOUTUBE_API_KEY when blank)" /></div>
            <div className="form-row checkbox-row"><label>
              <input type="checkbox" checked={newPlaylist.enabled}
                onChange={e => setNewPlaylist({ ...newPlaylist, enabled: e.target.checked })} /> Enabled
            </label></div>
            {/* dev24: opt-in per-source thumbnail migration to CF Images */}
            <div className="form-row checkbox-row"><label title="On each sync, upload the YouTube thumbnail to Cloudflare Images (16:9). Off by default. Roku-spec-compliant thumbnails will land in the catalog without a manual migration step.">
              <input type="checkbox" checked={newPlaylist.auto_migrate_thumbnails}
                onChange={e => setNewPlaylist({ ...newPlaylist, auto_migrate_thumbnails: e.target.checked })} /> Auto-migrate thumbnails to CF Images (16:9)
            </label></div>
            <div className="form-row checkbox-row"><label title="Ignore the day window and sync the full playlist. Best for static playlists that aren't updated frequently.">
              <input type="checkbox" checked={newPlaylist.sync_all}
                onChange={e => setNewPlaylist({ ...newPlaylist, sync_all: e.target.checked })} /> Sync entire playlist (ignore window)
            </label></div>
            <div className="yt-channel-edit-actions">
              <button className="btn-primary" onClick={handleAdd}><Save size={12} /> Add</button>
              <button className="btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="yt-add-toggle">
            <button className="btn-ghost" onClick={() => setShowAdd(true)}>
              <Plus size={14} /> Add Playlist
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
